// Sales tax — docs/MONEY_TAXES.md Phase 3.
//
// Owner, 2026-09-26: "a tab for taxes to track sales taxes we have to pay
// monthly … a section that tracks sales taxes we've collected".
//
//   GET    /api/office/taxes/sales?year=          financials.read
//   POST   /api/office/taxes/sales/returns         financials.read + export
//   PUT    /api/office/taxes/sales/returns/:id     (the payment)
//   DELETE /api/office/taxes/sales/returns/:id     (a record made by mistake)
//   PUT    /api/office/taxes/sales/settings        settings.update
//
// Every figure is the server's, in CENTS (server/utils/salesTax.js says what
// each one is). This screen adds nothing up. A return is recorded AS FILED:
// "Mark filed" with nothing changed files what the invoices say today, and
// from then on the screen shows the filing beside today's figures and the
// difference — it never quietly rewrites what was filed.
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, Pill, SectionLabel, Sheet, Stat } from '../../../ui/fw';
import { useCan } from '../../../hooks/usePermission';
import { get, post, put, del } from '../../shared/http';
import { fmtMoney, fmtDate, todayIso } from '../shared/format';
import { useAction, FormError, FormRow, SheetFooter } from '../records/OfficePage';
import { PageHeader, Refused, LoadFailed, Loading } from '../shared/Page';
import './SalesTax.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Cents → the screen's money. */
export const money = (cents) => fmtMoney((Number(cents) || 0) / 100);

/**
 * A dollar amount a person typed → whole cents, or null when it is not one.
 * "1,234.5" is 123450; "" and "abc" and "-3" are null — never a silent 0.
 */
export function dollarsToCents(text) {
  const s = String(text == null ? '' : text).replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return null;
  return Math.round(parseFloat(s) * 100);
}

/** "February 2026", "Q1 2026 (Jan–Mar)", "2026" — from the period's own dates. */
export function periodLabel(p) {
  const [y, sm] = p.start.split('-').map(Number);
  const em = Number(p.end.split('-')[1]);
  if (sm === em) return `${MONTHS[sm - 1]} ${y}`;
  if (sm === 1 && em === 12) return String(y);
  if (em - sm === 2) return `Q${Math.floor((sm - 1) / 3) + 1} ${y} (${MONTHS[sm - 1].slice(0, 3)}–${MONTHS[em - 1].slice(0, 3)})`;
  return `${fmtDate(p.start)} – ${fmtDate(p.end)}`;
}

/** What each status is called, and its colour. */
export const STATUS = {
  paid: { label: 'Paid', tone: 'ok' },
  filed: { label: 'Filed, not paid', tone: 'sched' },
  due: { label: 'Due', tone: 'warn' },
  overdue: { label: 'Past due', tone: 'stop' },
  open: { label: 'In progress', tone: 'now' },
  upcoming: { label: 'Not started', tone: 'neutral' },
};

export default function SalesTax() {
  const canRead = useCan('financials', 'read');
  const canFile = useCan('financials', 'export');
  const canSettings = useCan('settings', 'update');
  const [params, setParams] = useSearchParams();
  const thisYear = Number(todayIso().slice(0, 4));
  const rawYear = Number(params.get('year'));
  const year = Number.isInteger(rawYear) && rawYear >= 2000 && rawYear <= thisYear + 1 ? rawYear : thisYear;
  const [openKey, setOpenKey] = useState(null);

  const q = useQuery({
    queryKey: ['office', 'taxes', 'sales', year],
    queryFn: () => get(`/office/taxes/sales?year=${year}`),
    enabled: canRead,
  });
  const s = q.data;
  const open = s && openKey ? s.periods.find((p) => `${p.start}|${p.end}` === openKey) : null;

  const columns = useMemo(() => [
    { key: 'period', label: 'Period', role: 'title', render: (p) => <strong>{periodLabel(p)}</strong> },
    { key: 'due', label: 'Due', role: 'subtitle', render: (p) => `Due ${fmtDate(p.due)}` },
    { key: 'taxable', label: 'Taxable sales', align: 'right', role: 'fact', render: (p) => money(p.filed ? p.filed.taxable_sales_cents : p.computed.taxable) },
    { key: 'tax', label: 'Tax', align: 'right', role: 'fact', render: (p) => money(p.filed ? p.filed.tax_due_cents : p.computed.tax) },
    { key: 'status', label: 'Status', role: 'badge', render: (p) => <Pill tone={STATUS[p.status].tone} data-status={p.status}>{STATUS[p.status].label}</Pill> },
  ], []);

  if (!canRead) return <Refused what="sales tax" />;

  const years = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2].filter((y) => y >= 2000);
  const overdue = s ? s.periods.filter((p) => p.status === 'overdue') : [];
  const unpaid = s ? s.periods.filter((p) => p.payment_overdue) : [];
  const changed = s ? s.periods.filter((p) => p.difference && (p.difference.taxable !== 0 || p.difference.tax !== 0)) : [];
  const nextDue = s ? s.periods.find((p) => p.status === 'due' || p.status === 'overdue') : null;
  // A return filed at a frequency the company no longer uses stays on screen
  // as its own row (`other_frequency`), but its invoices are also inside a
  // period at today's frequency — counting both doubled them.
  const collected = s ? s.periods.reduce((n, p) => n + (p.start <= s.as_of && !p.other_frequency ? p.computed.tax : 0), 0) : 0;
  const owedToFile = s ? s.periods.filter((p) => ['due', 'overdue'].includes(p.status)).length : 0;

  return (
    <div className="of-tax" data-screen="office-SalesTax">
      <PageHeader title="Sales tax" meta={s ? `${year} · ${s.frequency} · ${s.basis} basis${s.rate != null ? ` · ${s.rate}%` : ''}` : String(year)} />
      <div className="of-filters">
        <FwField label="Year" htmlFor="of-tax-year">
          <FwSelect id="of-tax-year" value={String(year)} onChange={(e) => { const next = new URLSearchParams(params); next.set('year', e.target.value); setParams(next, { replace: true }); setOpenKey(null); }}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </FwSelect>
        </FwField>
      </div>
      {q.isPending ? <Loading lines={6} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="sales tax" /> : null}

      {s ? (
        <>
          {s.rate == null ? (
            <Notice tone="warn" data-note="no-rate">No company tax rate is set, so the screen cannot say what 6.35% of taxable sales would be. Set it under Admin → Settings.</Notice>
          ) : null}
          {overdue.length ? (
            <Notice tone="stop" data-note="overdue">
              {`${overdue.length === 1 ? 'A return is' : `${overdue.length} returns are`} past due and not recorded as filed: ${overdue.map(periodLabel).join(', ')}. Connecticut expects a return even when nothing is owed.`}
            </Notice>
          ) : null}
          {unpaid.length ? (
            <Notice tone="warn" data-note="unpaid">{`Filed but not recorded as paid, past the due date: ${unpaid.map(periodLabel).join(', ')}.`}</Notice>
          ) : null}
          {changed.length ? (
            <Notice tone="warn" data-note="changed">
              {`The invoices no longer match what was filed for ${changed.map(periodLabel).join(', ')}. Open the period to see the difference and which invoices it is in; an amended return may be needed.`}
            </Notice>
          ) : null}

          <div className="of-stats__row">
            <Stat data-stat="collected" label="Tax on invoices so far" value={money(collected)}
              hint={`${year}, ${s.basis === 'cash' ? 'by the day invoices were paid' : 'by the date on each invoice'}`} />
            <Stat data-stat="next-due" label="Next return" value={nextDue ? periodLabel(nextDue) : 'None due'}
              tone={nextDue ? (nextDue.status === 'overdue' ? 'stop' : 'warn') : 'ok'}
              hint={nextDue ? `${money(nextDue.computed.tax)} tax · due ${fmtDate(nextDue.due)}` : 'Every ended period is recorded as filed'} />
            <Stat data-stat="to-file" label="Returns to file" value={String(owedToFile)} tone={owedToFile ? 'warn' : 'neutral'} hint="Periods that have ended and are not recorded as filed" />
          </div>

          <section className="of-tax__section" data-section="periods">
            <SectionLabel>Returns</SectionLabel>
            <DataTable columns={columns} rows={s.periods} rowKey={(p) => `${p.start}|${p.end}`}
              caption={`Sales tax returns for ${year}`} onRowClick={(p) => setOpenKey(`${p.start}|${p.end}`)} />
            {s.basis === 'cash' ? (
              <p className="of-muted" data-note="cash">On cash basis a part-payment is in no period: the app does not record when one was made, so an invoice counts when it is paid off.</p>
            ) : null}
          </section>

          {s.cancelled.length ? (
            <section className="of-tax__section" data-section="cancelled">
              <SectionLabel>Sent, then cancelled</SectionLabel>
              <p className="of-muted">These were issued and later cancelled. If one was on a return already filed, the tax on it may be claimed back — ask the accountant.</p>
              <InvoiceList rows={s.cancelled} />
            </section>
          ) : null}

          {canSettings ? <FilingSettings settings={s} year={year} /> : null}
        </>
      ) : null}

      {open ? <PeriodSheet period={open} year={year} canFile={canFile} onClose={() => setOpenKey(null)} /> : null}
    </div>
  );
}

/** A short list of invoices, each one link to the invoice. */
function InvoiceList({ rows }) {
  const navigate = useNavigate();
  return (
    <ul className="of-tax__invoices">
      {rows.map((r) => (
        <li key={r.id}>
          <button type="button" className="of-tax__invoice" onClick={() => navigate(`/invoices/${r.id}`)} data-invoice={r.id}>
            <span className="of-tax__invoice-name"><strong>{r.invoice_number}</strong> {r.client_name || ''}</span>
            <span className="of-tax__invoice-fig">{fmtDate(r.dated)} · {money(r.sale)}{r.tax ? ` + ${money(r.tax)} tax` : ' · no tax'}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** One period: what the invoices say, what was filed, and the actions. */
function PeriodSheet({ period: p, year, canFile, onClose }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState(null); // null | 'file' | 'amend' | 'pay'
  const { busy, error, run, clear } = useAction();
  const c = p.computed;
  const f = p.filed;
  const refresh = () => qc.invalidateQueries({ queryKey: ['office', 'taxes', 'sales', year] });
  const close = () => { clear(); setMode(null); onClose(); };
  const differs = p.difference && (p.difference.taxable !== 0 || p.difference.tax !== 0);
  const ended = ['due', 'overdue', 'filed', 'paid'].includes(p.status);

  const remove = async () => {
    if (!f || !window.confirm(`Delete the record of ${f.amends_id ? 'this amendment' : 'this return'}? This removes it from the app only — it does not un-file anything with Connecticut.`)) return;
    const ok = await run(() => del(`/office/taxes/sales/returns/${f.id}`), 'Could not delete this record.');
    if (ok) { refresh(); close(); }
  };

  return (
    <Sheet open title={periodLabel(p)} onClose={close}>
      <div className="of-tax__sheet" data-period={`${p.start}|${p.end}`}>
        <FormError error={error} />
        <dl className="of-tax__figures" data-figures="computed">
          <div><dt>Sales (before tax)</dt><dd>{money(c.gross)}</dd></div>
          <div><dt>Exempt (no tax charged)</dt><dd>{money(c.exempt)}</dd></div>
          <div><dt>Taxable sales</dt><dd><strong>{money(c.taxable)}</strong></dd></div>
          <div><dt>Tax on the invoices</dt><dd><strong>{money(c.tax)}</strong></dd></div>
          {c.tax_at_rate != null && c.tax_at_rate !== c.tax ? (
            <div data-figure="at-rate"><dt>The company rate of taxable sales</dt><dd>{money(c.tax_at_rate)}</dd></div>
          ) : null}
          <div><dt>Invoices</dt><dd>{c.count}</dd></div>
        </dl>
        <p className="of-muted">{`Due ${fmtDate(p.due)}. ${p.other_frequency ? 'Filed at a frequency the company no longer uses. ' : ''}These are what the invoices say today.`}</p>

        {f ? (
          <section className="of-tax__filed" data-section="filed">
            <SectionLabel>{f.amends_id ? 'As amended' : 'As filed'}</SectionLabel>
            <dl className="of-tax__figures" data-figures="filed">
              <div><dt>Taxable sales</dt><dd>{money(f.taxable_sales_cents)}</dd></div>
              <div><dt>Tax due</dt><dd>{money(f.tax_due_cents)}</dd></div>
              <div><dt>Filed</dt><dd>{fmtDate(f.filed_on)}{f.confirmation ? ` · ${f.confirmation}` : ''}</dd></div>
              <div><dt>Paid</dt><dd>{f.paid_on ? `${fmtDate(f.paid_on)} · ${money(f.amount_paid_cents)}` : 'Not recorded'}</dd></div>
            </dl>
            {p.amendments.length ? <p className="of-muted">{`Amended ${p.amendments.length} time${p.amendments.length === 1 ? '' : 's'}; first filed ${fmtDate(p.original.filed_on)}.`}</p> : null}
            {differs ? (
              <Notice tone="warn" data-note="difference">
                {`Since this was filed the invoices changed by ${money(p.difference.taxable)} taxable and ${money(p.difference.tax)} tax. An amended return may be needed.`}
              </Notice>
            ) : null}
          </section>
        ) : null}

        {c.untaxed.length ? (
          <section data-section="untaxed">
            <SectionLabel>No tax charged, client not marked exempt</SectionLabel>
            <p className="of-muted">A forgotten rate or a real exemption — only you can tell. Mark the client tax-exempt if it is one.</p>
            <InvoiceList rows={c.untaxed} />
          </section>
        ) : null}
        {c.off_rate.length ? (
          <section data-section="off-rate">
            <SectionLabel>Charged a rate other than the company's</SectionLabel>
            <InvoiceList rows={c.off_rate} />
          </section>
        ) : null}
        {c.invoices.length ? (
          <details className="of-tax__all">
            <summary>{`All ${c.invoices.length} invoice${c.invoices.length === 1 ? '' : 's'} in this period`}</summary>
            <InvoiceList rows={c.invoices} />
          </details>
        ) : null}

        {canFile && !mode ? (
          <div className="of-tax__actions">
            {!f && ended ? <FwButton onClick={() => setMode('file')} data-action="mark-filed">Mark filed</FwButton> : null}
            {f ? <FwButton variant={f.paid_on ? 'ghost' : 'primary'} onClick={() => setMode('pay')} data-action="record-payment">{f.paid_on ? 'Change payment' : 'Record payment'}</FwButton> : null}
            {f ? <FwButton variant="ghost" onClick={() => setMode('amend')} data-action="amend">Record an amendment</FwButton> : null}
            {f ? <FwButton variant="ghost" onClick={remove} disabled={busy} data-action="delete-return">Delete this record</FwButton> : null}
          </div>
        ) : null}
        {mode === 'file' || mode === 'amend' ? (
          <FileForm period={p} amends={mode === 'amend' ? f : null} onDone={() => { refresh(); setMode(null); }} onCancel={() => setMode(null)} />
        ) : null}
        {mode === 'pay' ? <PayForm filed={f} onDone={() => { refresh(); setMode(null); }} onCancel={() => setMode(null)} /> : null}
      </div>
    </Sheet>
  );
}

/** Mark filed / record an amendment. The figures start as the invoices' and can be changed to what was actually filed. */
function FileForm({ period: p, amends, onDone, onCancel }) {
  const c = p.computed;
  const [form, setForm] = useState({
    filed_on: todayIso(),
    confirmation: '',
    taxable: (c.taxable / 100).toFixed(2),
    tax: (c.tax / 100).toFixed(2),
    notes: '',
  });
  const { busy, error, run, setError } = useAction();
  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const taxable = dollarsToCents(form.taxable);
    const tax = dollarsToCents(form.tax);
    if (taxable == null || tax == null) { setError('Taxable sales and tax due must be amounts, like 1234.56.'); return; }
    const body = {
      period_start: p.start, period_end: p.end, filed_on: form.filed_on,
      confirmation: form.confirmation.trim() || undefined, notes: form.notes.trim() || undefined,
      taxable_sales_cents: taxable, tax_due_cents: tax,
    };
    // Unchanged figures are the invoices' — send gross and exempt with them so
    // the record is whole; a changed figure means the person filed something
    // else, and only what they typed is theirs.
    if (taxable === c.taxable && tax === c.tax) { body.gross_sales_cents = c.gross; body.exempt_sales_cents = c.exempt; }
    if (amends) body.amends_id = amends.id;
    const ok = await run(() => post('/office/taxes/sales/returns', body), 'Could not record the return.');
    if (ok) onDone();
  };
  return (
    <form id="of-tax-file" className="or-form of-tax__form" onSubmit={submit} data-form={amends ? 'amend' : 'file'}>
      <SectionLabel>{amends ? 'Record an amended return' : 'Mark filed'}</SectionLabel>
      <FormError error={error} />
      <FormRow>
        <FwField label="Filed on" htmlFor="of-tax-filed-on" required><FwInput id="of-tax-filed-on" type="date" value={form.filed_on} onChange={set('filed_on')} required /></FwField>
        <FwField label="Confirmation number" htmlFor="of-tax-conf" hint="From myconneCT"><FwInput id="of-tax-conf" value={form.confirmation} onChange={set('confirmation')} /></FwField>
      </FormRow>
      <FormRow>
        <FwField label="Taxable sales filed" htmlFor="of-tax-taxable" required><FwInput id="of-tax-taxable" inputMode="decimal" value={form.taxable} onChange={set('taxable')} required /></FwField>
        <FwField label="Tax due filed" htmlFor="of-tax-tax" required><FwInput id="of-tax-tax" inputMode="decimal" value={form.tax} onChange={set('tax')} required /></FwField>
      </FormRow>
      <FwField label="Notes" htmlFor="of-tax-notes"><FwTextarea id="of-tax-notes" rows={2} value={form.notes} onChange={set('notes')} /></FwField>
      <p className="of-muted">The figures start as what the invoices say. Change them to exactly what was filed if it was different — the app keeps what you record, and shows any difference from the invoices later.</p>
      <SheetFooter onCancel={onCancel} busy={busy} submitLabel={amends ? 'Record amendment' : 'Mark filed'} formId="of-tax-file" />
    </form>
  );
}

/** Record (or change) the payment of a filed return. */
function PayForm({ filed, onDone, onCancel }) {
  const [form, setForm] = useState({
    paid_on: filed.paid_on || todayIso(),
    amount: ((filed.paid_on ? filed.amount_paid_cents : filed.tax_due_cents) / 100).toFixed(2),
  });
  const { busy, error, run, setError } = useAction();
  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const amount = dollarsToCents(form.amount);
    if (amount == null) { setError('The amount paid must be an amount, like 1234.56.'); return; }
    const ok = await run(() => put(`/office/taxes/sales/returns/${filed.id}`, { paid_on: form.paid_on, amount_paid_cents: amount }), 'Could not record the payment.');
    if (ok) onDone();
  };
  return (
    <form id="of-tax-pay" className="or-form of-tax__form" onSubmit={submit} data-form="pay">
      <SectionLabel>Record payment</SectionLabel>
      <FormError error={error} />
      <FormRow>
        <FwField label="Paid on" htmlFor="of-tax-paid-on" required><FwInput id="of-tax-paid-on" type="date" value={form.paid_on} onChange={set('paid_on')} required /></FwField>
        <FwField label="Amount paid" htmlFor="of-tax-amount" required hint="Include any penalty or interest paid with it"><FwInput id="of-tax-amount" inputMode="decimal" value={form.amount} onChange={set('amount')} required /></FwField>
      </FormRow>
      <SheetFooter onCancel={onCancel} busy={busy} submitLabel="Save payment" formId="of-tax-pay" />
    </form>
  );
}

/** How often, and on which basis, the company files. Admin only (settings.update). */
function FilingSettings({ settings, year }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ frequency: settings.frequency, basis: settings.basis });
  const { busy, error, run } = useAction();
  const [saved, setSaved] = useState(false);
  const dirty = form.frequency !== settings.frequency || form.basis !== settings.basis;
  const save = async (e) => {
    e.preventDefault();
    setSaved(false);
    const ok = await run(() => put('/office/taxes/sales/settings', form), 'Could not save the filing settings.');
    if (ok) { setSaved(true); qc.invalidateQueries({ queryKey: ['office', 'taxes', 'sales', year] }); }
  };
  return (
    <section className="of-tax__section" data-section="settings">
      <SectionLabel>How the company files</SectionLabel>
      <form className="or-form" onSubmit={save}>
        <FormError error={error} />
        <FormRow>
          <FwField label="Filing frequency" htmlFor="of-tax-frequency" hint="The one DRS assigned, in myconneCT">
            <FwSelect id="of-tax-frequency" value={form.frequency} onChange={(e) => setForm((v) => ({ ...v, frequency: e.target.value }))}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </FwSelect>
          </FwField>
          <FwField label="Basis" htmlFor="of-tax-basis" hint="Accrual unless DRS granted cash basis">
            <FwSelect id="of-tax-basis" value={form.basis} onChange={(e) => setForm((v) => ({ ...v, basis: e.target.value }))}>
              <option value="accrual">Accrual (by invoice date)</option>
              <option value="cash">Cash (by the day paid)</option>
            </FwSelect>
          </FwField>
        </FormRow>
        <div className="of-tax__actions">
          <FwButton type="submit" variant="ghost" loading={busy} disabled={!dirty} data-action="save-tax-settings">Save</FwButton>
          {saved && !dirty ? <span className="of-muted" data-saved>Saved. Returns already filed keep what they were filed on.</span> : null}
        </div>
      </form>
    </section>
  );
}
